import { useContext, useState } from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Badge } from 'design-system/src/components/Badge/Badge';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import { client, LetterOfMedicalNecessityTask, getRxFilePreviewUrl, TaskComment, TaskEvent } from '../../api';
import { TimelineItemList, TimelineItem } from '../TimelineItem/TimelineItem';
import { Sidebar, SidebarTopBar } from '../sidebar/Sidebar';
import { DrugPanel, PatientPanel, PrescriberPanel } from '../sidebar/SidebarPanels';
import { TaskContext } from './contexts';
import { ItemViewTopNav } from '../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../tableItemDetailViewComponents/Containers';
import { GeneralInfoTable } from '../GeneralInfoTable/GeneralInfoTable';
import LmnRespondedModal from './LmnRespondedModal/LmnRespondedModal';
import { FormActionContainer, StickyButtonContainer, StyledIframe } from './TaskActions';
import StructuredTaskSmallPrint from './StructuredTaskSmallPrint/StructuredTaskSmallPrint';

interface InProgressViewProps {
  instructions: string;
  documentId: string;
  rxId: string;
  setResponseType: (responseType: ResponseType) => void;
  responseType: ResponseType;
  submit: UseMutationResult<void, Error, ResponseType, unknown>;
}
const InProgressView = ({
  instructions,
  documentId,
  rxId,
  setResponseType,
  responseType,
  submit,
}: InProgressViewProps) => {
  return (
    <>
      <Typography styledAs="bodySmallDMSans">
        <RenderHtmlString>{instructions}</RenderHtmlString>
      </Typography>

      <Hairline />

      <InfoBox includeIcon={false} variant="surface">
        <Typography weight={600}>
          Please review the letter and select an option below within 3 days to proceed.
        </Typography>
      </InfoBox>

      <FormActionContainer>
        <StyledIframe src={getRxFilePreviewUrl(rxId, documentId)} />

        <StickyButtonContainer>
          <ButtonRow align={Alignment.SpaceBetween}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setResponseType('decline');
                submit.mutate('decline');
              }}
              disabled={submit.isPending && responseType !== 'decline'}
              isLoading={submit.isPending && responseType === 'decline'}
            >
              Decline letter and submit PA
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setResponseType('approve');
                submit.mutate('approve');
              }}
              disabled={submit.isPending && responseType !== 'approve'}
              isLoading={submit.isPending && responseType === 'approve'}
            >
              Approve letter and submit PA
            </Button>
          </ButtonRow>
        </StickyButtonContainer>
      </FormActionContainer>
    </>
  );
};

type ResponseType = 'approve' | 'decline';

/**
 * Version of TaskView that is used for LMN (Review Letter of Medical Necessity) tasks.
 */
const LetterOfMedicalNecessityTaskView: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showRespondedModal, setShowRespondedModal] = useState(false);
  const [responseType, setResponseType] = useState<ResponseType>('approve');
  const { taskQuery, taskId, onTaskUpdate } = useContext(TaskContext);

  const submit = useMutation<void, Error, ResponseType>({
    mutationFn: async (responseType: ResponseType) => {
      const formData = new FormData();
      formData.append('task_response', responseType === 'approve' ? 'approve_lmn' : 'decline_lmn');
      const res = await client.post(`tasks/${taskId}/respond`, formData);
      return res.data;
    },
    onSuccess: () => {
      onTaskUpdate();
      setShowRespondedModal(true);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  let body;
  let responseModal;
  if (taskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (taskQuery.error) {
    body = <PageLoadError />;
  } else if (taskQuery.data) {
    const { drug, patient, prescriber } = taskQuery.data;
    const task = taskQuery.data.task as LetterOfMedicalNecessityTask;

    if (!task.timeline || !task.timeline.length) {
      Sentry.captureMessage('No initial comment found for LMN task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
      body = <PageLoadError />;
    }

    const initialComment = task.timeline[0] as TaskComment;
    let completedTimeline = null;

    if (task.status === 'completed') {
      if (task.timeline.length === 1) {
        Sentry.captureMessage('No completion event found for LMN task', {
          level: 'error',
          extra: {
            taskId,
          },
        });
        body = <PageLoadError />;
      }
      const completionEvent = task.timeline[1] as TaskEvent;
      completedTimeline = (
        <TimelineItemList>
          <TimelineItem
            author={initialComment.user}
            timestamp={initialComment.created_at}
            message={initialComment.message}
            attachedFilesIds={initialComment.attachment_document_ids}
            isLastEventInList={false}
          />
          <TimelineItem
            author={completionEvent.user}
            timestamp={completionEvent.event_timestamp}
            action={completionEvent.action}
            message={completionEvent.label}
            isLastEventInList={true}
          />
        </TimelineItemList>
      );
    }

    responseModal = (
      <LmnRespondedModal
        closeModal={() => setShowRespondedModal(false)}
        patient={patient}
        drug={drug}
        variant={responseType}
      />
    );

    body = (
      <BodyContainer>
        <TopNavAndContent>
          <ItemViewTopNav
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            additionalRightSideContent={task.status === 'completed' && <Badge variant="neutral">Closed</Badge>}
          />

          <ContentOuterWrapper>
            <CenteredContent>
              <Typography styledAs="h6" marginBottom="1rem">
                {task.type}
              </Typography>
              <GeneralInfoTable
                patientName={drug.patient_name}
                prescriberName={drug.prescriber_name}
                dob={drug.dob}
                prescribedOn={drug.prescription_recieved_at}
                drugName={drug.name}
                prescriptionId={drug.id}
              />

              {task.status !== 'completed' ? (
                <InProgressView
                  instructions={initialComment.message}
                  documentId={initialComment.attachment_document_ids[0]}
                  rxId={drug.id}
                  setResponseType={setResponseType}
                  responseType={responseType}
                  submit={submit}
                />
              ) : (
                completedTimeline
              )}

              <StructuredTaskSmallPrint />
            </CenteredContent>
          </ContentOuterWrapper>
        </TopNavAndContent>

        {!isSidebarCollapsed && (
          <Sidebar>
            <SidebarTopBar />
            <DrugPanel drug={drug} showPharmaRelationshipNotice={true} />
            <PatientPanel patient={patient} />
            <PrescriberPanel prescriber={prescriber} />
          </Sidebar>
        )}
      </BodyContainer>
    );
  }

  return (
    <>
      {body}

      {showRespondedModal && responseType && responseModal}
    </>
  );
};

export default LetterOfMedicalNecessityTaskView;
